import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/index';
import { Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { apiService } from '../../services/api';
import type { ApiResponse } from '../../services/api';
import type { Cell, Money } from './types';

async function get<T>(path: string): Promise<T> {
	return ((await apiService.get(path)) as ApiResponse<T>).data;
}

/* ── formatting ─────────────────────────────────────────────────────────────── */

export const DASH = '—';
/** One unit per screen, so a column scans: ₹0.91L next to ₹6.17L, never ₹91.1k next to ₹6.17L. */
export const short = (n: Money) => {
	if (n === null) return DASH;
	if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
	return `₹${(n / 1e5).toFixed(2)}L`;
};
export const full = (n: Money) => (n === null ? DASH : `₹${Math.round(n).toLocaleString('en-IN')}`);
export const plain = (n: Money) => (n === null ? DASH : n.toLocaleString('en-IN'));
export const MONTHS = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];
export const monthLabel = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
export const monthShort = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

export const nowYm = new Date().toISOString().slice(0, 7);
export const fyOf = (ym: string) => {
	const y = Number(ym.slice(0, 4));
	const start = Number(ym.slice(5, 7)) >= 4 ? y : y - 1;
	return `${start}-${String(start + 1).slice(2)}`;
};
export const CURRENT_FY = fyOf(nowYm);
// Oldest sheet loaded is Nov 2023.
export const FY_OPTIONS = Array.from(
	{ length: Number(CURRENT_FY.slice(0, 4)) - 2023 + 1 },
	(_, i) => fyOf(`${2023 + i}-04`)
).reverse();

export const shiftMonth = (ym: string, by: number) => {
	const d = new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + by, 1);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Apr..Mar of the FY, cut at the current month. */
export const fyMonths = (fy: string) =>
	Array.from({ length: 12 }, (_, i) => shiftMonth(`${fy.slice(0, 4)}-04`, i)).filter(
		ym => ym <= nowYm
	);

/** Months from a to b inclusive. */
export const monthsBetween = (a: string, b: string) => {
	const out: string[] = [];
	for (let m = a; m <= b; m = shiftMonth(m, 1)) out.push(m);
	return out;
};

export const sumOrNull = (vals: Money[]) => {
	const have = vals.filter((v): v is number => v !== null);
	return have.length ? have.reduce((a, b) => a + b, 0) : null;
};

export const FINAL_REASON: Record<string, string> = {
	rule_folder: 'In the Salary Sheet folder',
	rule_last_before_payout: 'Last version before payment',
	manual: 'Chosen by a person',
};

export type Group = 'Head office' | 'Client sites' | 'Facilities';
export const GROUPS: Group[] = ['Head office', 'Client sites', 'Facilities'];
// ponytail: grouped by name; add a site_group column to wage_register_link when a name stops fitting.
export const groupOf = (site: string): Group =>
	/washing|facility/i.test(site)
		? 'Facilities'
		: /^head office|site staff/i.test(site)
			? 'Head office'
			: 'Client sites';

/* ── navigation ─────────────────────────────────────────────────────────────── */

/** Every level is a URL. Links are real <a>s, so ctrl-click opens a new tab. */
export const href = (p: { fy: string; site?: string; month?: string; person?: string }) => {
	const q = new URLSearchParams({ fy: p.fy });
	if (p.site) q.set('site', p.site);
	if (p.month) q.set('month', p.month);
	if (p.person) q.set('person', p.person);
	return `/payroll?${q.toString()}`;
};

export const linkCls = 'text-gray-900 hover:text-primary-700 hover:underline';
export const snCls = 'px-3 py-2 text-left text-gray-400 w-10 tabular-nums';

export const Crumbs: React.FC<{ items: { label: string; to?: string }[] }> = ({ items }) => (
	<nav className='flex items-center gap-1 text-[13px] text-gray-500 mb-3'>
		{items.map((c, i) => (
			<React.Fragment key={i}>
				{i > 0 && <ChevronRight className='w-3.5 h-3.5 text-gray-300' />}
				{c.to ? (
					<Link to={c.to} className={linkCls}>
						{c.label}
					</Link>
				) : (
					<span className='text-gray-900 font-medium'>{c.label}</span>
				)}
			</React.Fragment>
		))}
	</nav>
);

/** Prev / next arrows for a month, inside whatever view you are on. */
export const MonthStepper: React.FC<{ month: string; to: (m: string) => string }> = ({
	month,
	to,
}) => {
	const prev = shiftMonth(month, -1);
	const next = shiftMonth(month, 1);
	const btn = 'p-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50';
	return (
		<div className='flex items-center gap-1'>
			<Link
				to={to(prev)}
				className={btn}
				title={monthLabel(prev)}
				aria-label={`Previous month, ${monthLabel(prev)}`}
			>
				<ChevronLeft className='w-4 h-4 text-gray-600' />
			</Link>
			{next <= nowYm ? (
				<Link
					to={to(next)}
					className={btn}
					title={monthLabel(next)}
					aria-label={`Next month, ${monthLabel(next)}`}
				>
					<ChevronRight className='w-4 h-4 text-gray-600' />
				</Link>
			) : (
				<span className={`${btn} opacity-40 cursor-not-allowed`} title='No later month yet'>
					<ChevronRight className='w-4 h-4 text-gray-600' />
				</span>
			)}
		</div>
	);
};

/* ── small pieces ───────────────────────────────────────────────────────────── */

export const Chip: React.FC<{
	tone: 'green' | 'grey' | 'amber' | 'red' | 'blue';
	children: React.ReactNode;
}> = ({ tone, children }) => {
	const cls = {
		green: 'bg-green-50 text-green-700 border-green-200',
		grey: 'bg-gray-50 text-gray-500 border-gray-200',
		amber: 'bg-amber-50 text-amber-700 border-amber-200',
		red: 'bg-red-50 text-red-700 border-red-200',
		blue: 'bg-blue-50 text-blue-700 border-blue-200',
	}[tone];
	return (
		<span
			className={`inline-block px-1.5 py-0.5 text-[11px] rounded border whitespace-nowrap ${cls}`}
		>
			{children}
		</span>
	);
};

export const Loading = () => (
	<div className='flex items-center gap-2 text-gray-500 text-sm py-10 justify-center'>
		<Loader2 className='w-4 h-4 animate-spin' /> Loading…
	</div>
);

export const ErrorLine: React.FC<{ msg: string }> = ({ msg }) => (
	<div className='text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3'>{msg}</div>
);

export const Card: React.FC<{
	title?: string;
	right?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
}> = ({ title, right, children, className = '' }) => (
	<div className={`border border-gray-200 rounded-xl bg-white ${className}`}>
		{(title || right) && (
			<div className='flex items-center justify-between px-4 pt-3 pb-2'>
				<div className='text-[13px] font-semibold text-gray-800'>{title}</div>
				{right}
			</div>
		)}
		{children}
	</div>
);

/** A headline number. `sub` explains it in plain words. */
export const Tile: React.FC<{
	label: string;
	value: string;
	sub?: React.ReactNode;
	to?: string;
}> = ({ label, value, sub, to }) => {
	const body = (
		<>
			<div className='text-[12px] text-gray-500'>{label}</div>
			<div
				className={`text-2xl font-semibold tracking-tight mt-0.5 ${value === DASH ? 'text-gray-300' : 'text-gray-900'}`}
			>
				{value}
			</div>
			{sub && <div className='text-[12px] text-gray-500 mt-0.5'>{sub}</div>}
		</>
	);
	const cls = 'border border-gray-200 rounded-xl bg-white px-4 py-3 block';
	return to ? (
		<Link to={to} className={`${cls} hover:border-primary-300 hover:shadow-sm transition`}>
			{body}
		</Link>
	) : (
		<div className={cls}>{body}</div>
	);
};

export interface BarItem {
	month: string;
	value: Money;
	to?: string;
	sub?: string;
	/** Wage month not paid yet (paid next month), so the sheets are still coming in. */
	inProgress?: boolean;
}

/**
 * Net pay per wage month for one site. One hue, thin bars, the amount written on top (7-12 bars, so every
 * label fits). The month still being paid is drawn hatched.
 */
export const MonthBars: React.FC<{ items: BarItem[]; height?: number }> = ({
	items,
	height = 110,
}) => {
	const max = Math.max(1, ...items.map(i => i.value || 0));
	return (
		<div className='flex items-stretch px-4 pb-4 pt-2'>
			{items.map(it => {
				const h = it.value === null ? 0 : Math.max(3, (it.value / max) * height);
				const inner = (
					<>
						<div
							className={`text-[12px] font-semibold tabular-nums ${it.value === null ? 'text-gray-300' : 'text-gray-900'}`}
						>
							{it.value === null ? DASH : short(it.value)}
						</div>
						<div className='w-full flex items-end justify-center mt-1' style={{ height }}>
							{it.value === null ? (
								<div className='w-10 h-1 rounded bg-gray-100' />
							) : (
								<div
									className={`w-10 rounded-t transition-colors ${
										it.inProgress
											? 'bg-primary-200 group-hover:bg-primary-300'
											: 'bg-primary-500 group-hover:bg-primary-700'
									}`}
									style={{
										height: h,
										backgroundImage: it.inProgress
											? 'repeating-linear-gradient(135deg, rgba(255,255,255,.7) 0 3px, transparent 3px 7px)'
											: undefined,
									}}
								/>
							)}
						</div>
						<div className='w-full border-t border-gray-200 pt-1.5 text-[12px] text-gray-700 font-medium'>
							{monthShort(it.month)}
						</div>
						{it.sub && <div className='text-[11px] text-gray-500'>{it.sub}</div>}
					</>
				);
				const cls =
					'group flex-1 min-w-0 flex flex-col items-center text-center rounded-lg py-1 hover:bg-gray-50 transition';
				return it.to ? (
					<Link key={it.month} to={it.to} className={cls} title={`Open ${monthLabel(it.month)}`}>
						{inner}
					</Link>
				) : (
					<div key={it.month} className={cls}>
						{inner}
					</div>
				);
			})}
		</div>
	);
};

export function useLoad<T>(path: string | null) {
	const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({
		data: null,
		error: null,
		loading: true,
	});
	useEffect(() => {
		if (!path) return;
		let live = true;
		setState({ data: null, error: null, loading: true });
		get<T>(path)
			.then(data => live && setState({ data, error: null, loading: false }))
			.catch(
				(err: { message?: string }) =>
					live &&
					setState({
						data: null,
						error: `Could not load payroll data: ${err?.message || 'the server did not answer'}`,
						loading: false,
					})
			);
		return () => {
			live = false;
		};
	}, [path]);
	return state;
}

/** The FY matrix, indexed. Overview, Site and Month views all read this. */
export function useMatrix(fy: string) {
	const months = fyMonths(fy);
	const load = useLoad<{ cells: Cell[] }>(
		`/payroll/matrix?from=${months[0]}&to=${months[months.length - 1]}`
	);
	const index = useMemo(() => {
		const cells = load.data?.cells || [];
		const byKey = new Map(cells.map(c => [`${c.site}|${c.wage_month}`, c]));
		const summary = new Set(cells.filter(c => c.is_summary).map(c => c.site));
		const siteTotal = (s: string) =>
			sumOrNull(cells.filter(c => c.site === s).map(c => c.net_pay)) || 0;
		const sites = Array.from(new Set(cells.map(c => c.site)))
			.filter(s => !summary.has(s))
			.sort((a, b) => siteTotal(b) - siteTotal(a));
		return { cells, byKey, sites, summarySites: Array.from(summary) };
	}, [load.data]);
	const cell = (s: string, m: string) => index.byKey.get(`${s}|${m}`);
	return { ...load, ...index, months, cell };
}

/* ── access ─────────────────────────────────────────────────────────────────── */

/** Payroll shows salaries: only user types granted the 'payroll' menu may open it, even by typing the URL. */
export const usePayrollAccess = () =>
	useSelector((s: RootState) => s.auth.user?.menuPermissions?.['payroll']?.access === true);

export const NoPayrollAccess = () => (
	<div className='max-w-md mx-auto mt-24 text-center text-[13px] text-gray-600'>
		<h1 className='text-lg font-semibold text-gray-900 mb-1'>No access to Payroll</h1>
		<p>
			Payroll shows salaries, so it is open only to people who are given access. Ask an admin if you
			need it.
		</p>
	</div>
);
